import { Link } from 'react-router'
import { JsonLd } from '#app/components/json-ld.tsx'
import {
	findPostBySlug,
	formatPostDate,
	getPostBySlug,
} from '#app/utils/blog.ts'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/$slug.ts'

export function loader({ params }: Route.LoaderArgs) {
	const post = getPostBySlug(params.slug)
	if (!post) {
		// Drafts and future-dated posts are indistinguishable from missing ones
		// in production - deliberately, so an unpublished URL leaks nothing.
		throw new Response('Not found', { status: 404, statusText: 'Not Found' })
	}
	return { meta: post.meta }
}

// `private`, not `public`: the root loader embeds session data in the
// document, so a shared cache must never store this response.
export const headers: Route.HeadersFunction = () => ({
	'Cache-Control': `private, max-age=${5 * 60}`,
})

export const links: Route.LinksFunction = () => [
	{
		rel: 'alternate',
		type: 'application/rss+xml',
		title: 'Syrcha Blog',
		href: '/rss.xml',
	},
]

export const meta: Route.MetaFunction = ({ data, matches }) => {
	if (!data) return [{ title: 'Post not found | Syrcha' }]
	return buildMeta({
		title: `${data.meta.title} | Syrcha`,
		description: data.meta.description,
		matches,
		...(data.meta.ogImage ? { image: data.meta.ogImage } : {}),
	})
}

export default function BlogPostRoute({
	loaderData,
	params,
}: Route.ComponentProps) {
	// Unfiltered on purpose - the loader above is the gate. See findPostBySlug.
	const post = findPostBySlug(params.slug)
	if (!post) return null

	const { meta } = loaderData
	const Content = post.Content
	const origin = 'https://syrcha.com'
	const url = `${origin}/blog/${meta.slug}`

	return (
		// The marketing layout owns the single `<main>` landmark.
		<div className="bg-[var(--marketing-base)]">
			<article className="pt-12 pb-20 sm:pt-16">
				{/*
				  JSON-LD is rendered here, nested inside the article. It must not move
				  into `meta` or up to a direct child of <body>: React 19 treats
				  document-level <script> nodes as resources rather than hydrating
				  them, which ships a duplicate copy of the schema.
				*/}
				<JsonLd
					data={{
						'@context': 'https://schema.org',
						'@type': 'BlogPosting',
						headline: meta.title,
						description: meta.description,
						datePublished: meta.publishedAt,
						dateModified: meta.updatedAt ?? meta.publishedAt,
						author: { '@type': 'Organization', name: meta.author },
						publisher: {
							'@type': 'Organization',
							name: 'Syrcha',
							logo: `${origin}/favicon.svg`,
						},
						mainEntityOfPage: { '@type': 'WebPage', '@id': url },
						url,
						...(meta.ogImage ? { image: `${origin}${meta.ogImage}` } : {}),
					}}
				/>
				<JsonLd
					data={{
						'@context': 'https://schema.org',
						'@type': 'BreadcrumbList',
						itemListElement: [
							{
								'@type': 'ListItem',
								position: 1,
								name: 'Home',
								item: origin,
							},
							{
								'@type': 'ListItem',
								position: 2,
								name: 'Blog',
								item: `${origin}/blog`,
							},
							{ '@type': 'ListItem', position: 3, name: meta.title, item: url },
						],
					}}
				/>

				<div className="mx-auto max-w-2xl px-6">
					<nav aria-label="Breadcrumb">
						<ol className="flex flex-wrap items-center gap-2 font-[family-name:var(--font-mono)] text-[12px] text-[var(--marketing-faint)]">
							<li>
								<Link
									to="/"
									className="inline-flex min-h-6 items-center transition-colors hover:text-[var(--marketing-accent)]"
								>
									Home
								</Link>
							</li>
							<li aria-hidden="true">/</li>
							<li>
								<Link
									to="/blog"
									className="inline-flex min-h-6 items-center transition-colors hover:text-[var(--marketing-accent)]"
								>
									Blog
								</Link>
							</li>
						</ol>
					</nav>

					{meta.draft ? (
						<p className="mt-6 rounded-lg border border-[var(--marketing-warn)] px-4 py-3 font-[family-name:var(--font-mono)] text-[12px] text-[var(--marketing-warn)]">
							Draft: visible in development only. Set <code>draft: false</code>{' '}
							to publish.
						</p>
					) : null}

					<header className="mt-7">
						<h1 className="font-[family-name:var(--font-display)] text-[32px] leading-[1.08] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]">
							{meta.title}
						</h1>
						<div className="mt-5 flex flex-wrap items-center gap-3 font-[family-name:var(--font-mono)] text-[12px] text-[var(--marketing-faint)]">
							<time dateTime={meta.publishedAt}>
								{formatPostDate(meta.publishedAt)}
							</time>
							<span aria-hidden="true">·</span>
							<span>{meta.author}</span>
							{meta.readingMinutes ? (
								<>
									<span aria-hidden="true">·</span>
									<span>{meta.readingMinutes} min read</span>
								</>
							) : null}
						</div>
					</header>

					<div className="prose-post mt-10">
						<Content />
					</div>

					<footer className="mt-14 border-t border-[var(--marketing-border)] pt-8">
						<Link
							to="/blog"
							className="inline-flex min-h-11 items-center font-[family-name:var(--font-mono)] text-[13px] text-[var(--marketing-accent)] hover:underline"
						>
							← All posts
						</Link>
					</footer>
				</div>
			</article>
		</div>
	)
}
